// Blocks drawn the same in chat cards (components/AppCard.tsx) and in full views (the kit's Badge, Stat, Stats and
// Footnote): toned pills, stat tiles and footnotes. Styles: blocks.css (shared too). Tones are class names that pick
// theme variables, never colours from an app; a trend adds ▲/▼ and "up"/"down" for screen readers.
import type { ReactNode } from "react";
import { formatViewValue, isTone, isTrend, progressShare, rankMark, statColumns, type Tone, type Trend, type ViewPillFormat } from "@bloom/shared";

export const toneClass = (tone?: unknown) => (isTone(tone) ? ` k-tone-${tone}` : "");

/** ▲ or ▼, read as "up" or "down". */
export function TrendMark({ trend }: { trend?: unknown }) {
  if (!isTrend(trend)) return null;
  return (
    <>
      <span className="k-trend" aria-hidden="true">{trend === "up" ? "▲" : "▼"}</span>
      <span className="k-sr">{trend} </span>
    </>
  );
}

/** Text with an arrow already at its start ("▲ 4%") loses it when a trend draws one. */
const bare = (v: ReactNode, trend?: unknown) => (isTrend(trend) && typeof v === "string" ? v.replace(/^[▲▼]\s*/, "") : v);

/** A badge or pill: a short label, optionally toned, optionally with a trend. */
export function Pill({ tone, trend, className = "", children }: { tone?: Tone | string; trend?: Trend; className?: string; children: ReactNode }) {
  return (
    <span className={`k-badge${toneClass(tone)}${className ? ` ${className}` : ""}`} data-tone={isTone(tone) ? tone : undefined}>
      <TrendMark trend={trend} />
      {bare(children, trend)}
    </span>
  );
}

/** A stat tile: a big value over a small label. */
export function StatTile({ label, value, tone, trend, hint }: { label: ReactNode; value: ReactNode; tone?: Tone | string; trend?: Trend; hint?: ReactNode }) {
  return (
    <div className="k-stat" data-tone={isTone(tone) ? tone : undefined}>
      {/* Long values ("258.30–271.20") step down a size so they fit a tile 3 across. */}
      <div className={`k-stat-value${toneClass(tone)}${typeof value === "string" && value.length > 9 ? " long" : ""}`} title={typeof value === "string" ? value : undefined}>
        <TrendMark trend={trend} />
        {bare(value, trend)}
      </div>
      <div className="k-stat-label">{label}</div>
      {hint && <div className="k-stat-hint">{hint}</div>}
    </div>
  );
}

/** Tiles in a grid: up to 3 across (4 make a 2 × 2), 2 on phones (statColumns). */
export function StatGrid({ count, label, className = "", children }: { count: number; label?: string; className?: string; children: ReactNode }) {
  const style = { "--cols": statColumns(count), "--cols-narrow": statColumns(count, true) } as React.CSSProperties;
  return (
    <div className={`k-stats${className ? ` ${className}` : ""}`} style={style} role="group" aria-label={label}>
      {children}
    </div>
  );
}

export function FootnoteLine({ className = "", children }: { className?: string; children: ReactNode }) {
  return <div className={`k-footnote${className ? ` ${className}` : ""}`}>{children}</div>;
}

// ---- More blocks: fields, progress bars, charts, lists, tile grids, people (shared/src/apps.ts Card) ----

/** Label–value rows ("When" Sat 7pm). */
export function FieldRows({ items, className = "" }: { items: { label: ReactNode; value: ReactNode }[]; className?: string }) {
  return (
    <dl className={`k-fields-list${className ? ` ${className}` : ""}`}>
      {items.map((f, i) => (
        <div key={i} className="k-field-row">
          <dt>{f.label}</dt>
          <dd title={typeof f.value === "string" ? f.value : undefined}>{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** One progress bar: a label, its text on the right ("68%", "3/5", or its own), and the bar in a tone. */
export function ProgressRow({ label, value, max, text, tone }: { label: ReactNode; value: number; max?: number; text?: string; tone?: Tone | string }) {
  const p = progressShare({ value, max, text });
  return (
    <div className={`k-progress${toneClass(tone)}`}>
      <div className="k-progress-head">
        <span className="k-progress-label">{label}</span>
        <span className="k-progress-text">{p.text}</span>
      </div>
      <div className="k-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(p.share * 100)} aria-label={typeof label === "string" ? `${label}: ${p.text}` : p.text}>
        <span className="k-progress-fill" style={{ width: `${p.share * 100}%` }} />
      </div>
    </div>
  );
}

/**
 * A small chart: bars to scale from zero (or from the lowest value when there are negatives), or a line from min to
 * max with its area shaded. Read aloud as its caption and each "label value". Fixed height, so cards reserve it.
 */
export function MiniChart({ type, points, tone, caption, format, height = 80 }: { type: "bar" | "line"; points: { label: string; value: number; tone?: Tone | string }[]; tone?: Tone | string; caption?: string; format?: ViewPillFormat; height?: number }) {
  if (points.length < 2) return null;
  const values = points.map((p) => p.value);
  const spoken = `${caption ? `${caption}: ` : ""}${points.map((p) => `${p.label} ${formatViewValue(p.value, format)}`.trim()).join(", ")}`;
  const labels = points.length <= 8 ? points.map((p) => p.label) : points.map((p, i) => (i === 0 || i === points.length - 1 ? p.label : ""));
  const max = Math.max(...values);
  const min = Math.min(...values);
  let plot: ReactNode;
  if (type === "bar") {
    const lo = Math.min(0, min);
    const span = max - lo || 1;
    plot = (
      <div className="k-chart-bars" style={{ height }}>
        {points.map((p, i) => (
          <span key={i} className={`k-chart-bar${toneClass(p.tone ?? tone)}`} style={{ height: `${Math.max(2, ((p.value - lo) / span) * 100)}%` }} title={`${p.label} ${formatViewValue(p.value, format)}`.trim()} />
        ))}
      </div>
    );
  } else {
    const span = max - min || 1;
    const xy = points.map((p, i) => [(i / (points.length - 1)) * 100, 96 - ((p.value - min) / span) * 92] as const);
    const line = xy.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
    const [lx, ly] = xy[xy.length - 1]!;
    plot = (
      <div className={`k-chart-line${toneClass(tone)}`} style={{ height }}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <polygon points={`0,100 ${line} 100,100`} className="k-chart-area" />
          <polyline points={line} className="k-chart-stroke" vectorEffect="non-scaling-stroke" />
        </svg>
        <span className="k-chart-dot" style={{ left: `${lx}%`, top: `${ly}%` }} />
      </div>
    );
  }
  return (
    <figure className="k-chart" role="img" aria-label={spoken}>
      {plot}
      <div className={`k-chart-labels${type === "line" || points.length > 8 ? " ends" : ""}`} aria-hidden="true">
        {(type === "line" || points.length > 8 ? [labels[0], labels[labels.length - 1]] : labels).map((l, i) => <span key={i}>{l}</span>)}
      </div>
      {caption && <figcaption className="k-caption">{caption}</figcaption>}
    </figure>
  );
}

/** A short list: plain, ranked (🥇🥈🥉 4. 5.) or a checklist (✓ done, ○ not yet), with optional values on the right. */
export function BlockList({ items, style = "plain", className = "" }: { items: { text: ReactNode; value?: ReactNode; done?: boolean; tone?: Tone | string }[]; style?: "plain" | "ranked" | "check"; className?: string }) {
  return (
    <ol className={`k-blist ${style}${className ? ` ${className}` : ""}`}>
      {items.map((it, i) => (
        <li key={i} className={it.done ? "done" : undefined}>
          {style === "ranked" && <span className="k-blist-mark" aria-label={`${i + 1}.`}>{rankMark(i)}</span>}
          {style === "check" && (
            <span className={`k-blist-mark${it.done ? " k-tone-positive" : ""}`} aria-hidden="true">{it.done ? "✓" : "○"}</span>
          )}
          <span className="k-blist-text">
            {style === "check" && <span className="k-sr">{it.done ? "Done: " : "Not done: "}</span>}
            {it.text}
          </span>
          {it.value !== undefined && it.value !== "" && <span className={`k-blist-value${toneClass(it.tone)}`}>{it.value}</span>}
        </li>
      ))}
    </ol>
  );
}

/** Rows of small lettered tiles in tones (a Wordle board, tic-tac-toe, seats). */
export function TileGrid({ rows, caption, label, className = "" }: { rows: { text?: string; tone?: Tone | string }[][]; caption?: string; label?: string; className?: string }) {
  const spoken = rows.map((r) => r.map((c) => `${c.text ?? "blank"}${isTone(c.tone) ? ` ${c.tone}` : ""}`).join(", ")).join("; ");
  return (
    <figure className={`k-tiles${className ? ` ${className}` : ""}`} role="img" aria-label={`${label ?? caption ?? "Grid"}: ${spoken}`}>
      {rows.map((r, i) => (
        <div key={i} className="k-tiles-row" aria-hidden="true">
          {r.map((c, j) => <span key={j} className={`k-tile${toneClass(c.tone)}`}>{c.text}</span>)}
        </div>
      ))}
      {caption && <figcaption className="k-caption">{caption}</figcaption>}
    </figure>
  );
}

/** Faces in a row (overlapping), "+N" past `shown`, then "Going · 5". `faces` are already-drawn avatars. */
export function PeopleRow({ faces, label, total, shown = 8 }: { faces: ReactNode[]; label?: string; total?: number; shown?: number }) {
  const n = total ?? faces.length;
  return (
    <div className="k-people">
      <span className="k-people-faces">
        {faces.slice(0, shown).map((f, i) => <span key={i} className="k-people-face">{f}</span>)}
        {n > shown && <span className="k-people-more">+{n - shown}</span>}
      </span>
      <span className="k-people-label">{label ? `${label} · ${n}` : `${n} ${n === 1 ? "person" : "people"}`}</span>
    </div>
  );
}
